/** 星闪 DK63 V2：单灯位 / 双灯位（与公版 DK63 keyboardType=63 共用类型，须用 PID 区分） */
export const XINGSHAN_DK63V2_VENDOR_ID = 0x1ca6; // 7334
export const XINGSHAN_DK63V2_PRODUCT_IDS = [0x1c4d, 0x1c49] as const; // 7245 单灯 / 7241 双灯

/** 华奋达 DK63Star：有线单灯，虚拟键盘矩阵对齐星闪 DK63 V2（keyboardType=63） */
export const HUAFENDA_DK63STAR_VENDOR_ID = 0x0c45; // 3141
export const HUAFENDA_DK63STAR_PRODUCT_ID = 0x80f8; // 33016

/** 公版 DK63（非 V2）底行右簇：RAlt、←、↓、→、Fn */
export const DK63_BOTTOM_ROW_TRAILING = [230, 80, 81, 79, 61697] as const;

/** DK63 V2 底行右簇：RAlt、Fn、←、↓、→（与实物/SDK 一致；华奋达 DK63Star 同序） */
export const DK63V2_BOTTOM_ROW_TRAILING = [230, 61697, 80, 81, 79] as const;

export function isXingshanDk63V2Device(
  productId?: number | string,
  vendorId?: number | string
): boolean {
  const vid = Number(vendorId);
  const pid = Number(productId);
  if (!Number.isFinite(vid) || !Number.isFinite(pid)) return false;
  return (
    vid === XINGSHAN_DK63V2_VENDOR_ID &&
    (XINGSHAN_DK63V2_PRODUCT_IDS as readonly number[]).includes(pid)
  );
}

/** 华奋达 DK63Star（0C45:80F8），单灯 */
export function isHuafendaDk63StarLayoutDevice(
  productId?: number | string,
  vendorId?: number | string,
  identifier?: string
): boolean {
  const pid = Number(productId);
  if (Number.isFinite(pid) && pid === HUAFENDA_DK63STAR_PRODUCT_ID) {
    const vid = Number(vendorId);
    if (!Number.isFinite(vid) || vid === HUAFENDA_DK63STAR_VENDOR_ID) {
      return true;
    }
  }
  const id = String(identifier ?? "");
  if (id.includes("33016") && /dk63\s*star/i.test(id)) return true;
  // equipmentData 缺 PID 时：华奋达 + DK63 Star 名称仍视为本机
  if (
    /huafenda/i.test(id) &&
    (/\bdk63\s*star\b/i.test(id) || /\bdk63star\b/i.test(id))
  ) {
    return true;
  }
  return false;
}

/** 使用 DK63 V2 底行序的机型：星闪 DK63 V2 + 华奋达 DK63Star */
export function usesDk63V2BottomRowLayout(
  productId?: number | string,
  vendorId?: number | string,
  identifier?: string
): boolean {
  return (
    isXingshanDk63V2Device(productId, vendorId) ||
    isHuafendaDk63StarLayoutDevice(productId, vendorId, identifier)
  );
}

/**
 * 在公版 63 矩阵上套用 DK63 V2 底行（仅 WASD 槽位定位等场景使用，勿改共享 KEYBOARD_LAYOUTS["63"]）。
 * 底行 col9–13：RAlt、Fn、←、↓、→
 */
export function applyDk63V2BottomRowToLayout63(
  matrix: number[][] | undefined
): number[][] | undefined {
  if (!matrix?.length) return matrix;
  return matrix.map((row, rowIndex) => {
    if (rowIndex !== 5 || !row?.length || row.length < 14) return row;
    const next = row.slice();
    next[9] = DK63V2_BOTTOM_ROW_TRAILING[0];
    next[10] = DK63V2_BOTTOM_ROW_TRAILING[1];
    next[11] = DK63V2_BOTTOM_ROW_TRAILING[2];
    next[12] = DK63V2_BOTTOM_ROW_TRAILING[3];
    next[13] = DK63V2_BOTTOM_ROW_TRAILING[4];
    return next;
  });
}

/**
 * Shift 行 col11(/? 位) 与 col13(R-Shift 位) 键宽互换（相对公版 TOTAL_63：1 ↔ 1.9）。
 * 星闪 DK63 V2、华奋达 DK63Star 共用；公版 DK63 不改。
 * （DK63Star 的键码位置互换是矩阵事，列宽仍按「左窄右宽」互换为左 1.9 / 右 1。）
 */
export function applyDk63V2SlashRShiftKeyWidths(
  widths: number[][] | undefined,
  productId?: number | string,
  vendorId?: number | string,
  identifier?: string
): number[][] | undefined {
  if (!widths?.length) return widths;
  if (!usesDk63V2BottomRowLayout(productId, vendorId, identifier)) {
    return widths;
  }
  const next = widths.map((row) => row.slice());
  const row4 = next[4];
  if (!row4 || row4.length < 14) return widths;
  // 公版 col11=/?(1) ↔ col13=R-Shift(1.9) → 1.9 / 1
  row4[11] = 1.9;
  row4[12] = 1;
  row4[13] = 1;
  return next;
}
